import { CloseIcon } from "vega-icons";

import { BulkActions } from "@vega-core/components/BulkActions/BulkActions";
import { ThemedIconButton } from "@vega-core/components/Buttons/ThemedIconButton";
import { Notice, NoticeType } from "@vega-core/components/Notice/Notice";
import { m } from "@vega-core/paraglide/messages";
import { buildTimeFlags } from "@vega-core/utils/buildTimeFlags";
import { isGPCoInvestPortal } from "@vega-core/utils/global";
import { useFeatureFlagGuard } from "@vega-modules/hooks/useFeatureFlagGuard";
import { CommonPaths } from "@vega-modules/paths";

import { CoInvestmentPageHeader } from "../components/CoInvestmentPageHeader/CoInvestmentPageHeader";
import { useOutreachManagement } from "../components/OutreachTable/hooks/useOutreachManagement";
import { OutreachTable } from "../components/OutreachTable/OutreachTable";
import { ContactsModal } from "../components/ProspectsTable/ContactsModal";
import { EmailEditModal } from "../components/ProspectsTable/EmailEditModal";
import { OpportunityOwnerModal } from "../components/ProspectsTable/OpportunityOwnerModal";
import { ShareModal } from "../components/ShareModal/ShareModal";
import { UpdateDataPointsModal } from "../components/TrackingTable/components/UpdateDataPointsModal";

export const OutreachPage = () => {
  useFeatureFlagGuard(
    "gp_co_investment",
    // Only redirect to home when not in GP co-invest portal
    isGPCoInvestPortal ? undefined : CommonPaths.Home,
  );
  const {
    dealId,
    dealName,
    prospectsData,
    isLoading,
    isShareModalOpen,
    selectedProspectForShare,
    handleShare,
    handleCloseShareModal,
    isEmailEditModalOpen,
    selectedProspectsForEmailEdit,
    handleEditEmail,
    closeEmailEditModal,
    canAllSelectedProspectsSendDeal,
    handleCopyLink,
    handleAddOpportunityOwner,
    rowSelection,
    selectedCount,
    isOpportunityOwnerModalOpen,
    selectedProspectsForOpportunityOwner,
    closeOpportunityOwnerModal,
    isUpdateDataPointsModalOpen,
    closeUpdateDataPointsModal,
    dealOpportunityIdForEditingDataPoints,
    handleUpdateDataPoints,
    setRowSelection,
    isContactsModalOpen,
    selectedProspectForContacts,
    handleOpenContactsModal,
    handleCloseContactsModal,
    isCopyLinkNoticeOpen,
    dismissCopyLinkNotice,
    paginationElement,
  } = useOutreachManagement();

  return (
    <>
      <div className="mt-1">
        <CoInvestmentPageHeader
          capitalize={false}
          description={
            <p className="text-text/secondary body-base-regular">
              {m.coInvestments_outreach_description()}
            </p>
          }
          fundName={dealName}
          isLoading={isLoading}
          title={m.coInvestments_nav_outreach()}
        />

        <div className="flex flex-col space-y-6">
          {prospectsData.length > 0 && (
            <Notice
              content={m.coInvestments_youShouldGrantDataRoomAccess()}
              type={NoticeType.WARNING}
            />
          )}

          {isCopyLinkNoticeOpen ? (
            <Notice
              action={
                <ThemedIconButton
                  ariaLabel={m.btn_close()}
                  className="ml-4"
                  dataTestId="copy-link"
                  icon={<CloseIcon />}
                  onClick={dismissCopyLinkNotice}
                  variant="noBackground"
                />
              }
              content={m.coInvestments_outreach_copyLink_AccessInfo()}
              dataTestId="copy-link"
              type={NoticeType.PENDING}
            />
          ) : null}
        </div>

        <div className="space-y-4 mt-6 mb-48">
          <OutreachTable
            dealId={dealId}
            isLoading={isLoading}
            onAddOpportunityOwner={handleAddOpportunityOwner}
            onCopyLink={handleCopyLink}
            onOpenContactsModal={handleOpenContactsModal}
            onRowSelectionChange={setRowSelection}
            onShare={handleShare}
            onUpdateDataPoints={
              buildTimeFlags.gp_co_investment_update_salesforce_data
                ? handleUpdateDataPoints
                : undefined
            }
            paginationElement={paginationElement}
            prospectsData={prospectsData}
            rowSelection={rowSelection}
          />
        </div>
      </div>

      {selectedProspectsForEmailEdit.length > 0 && isEmailEditModalOpen ? (
        <EmailEditModal
          closeModal={closeEmailEditModal}
          dealId={dealId}
          dealName={dealName}
          prospects={selectedProspectsForEmailEdit}
        />
      ) : null}

      {selectedProspectForShare ? (
        <ShareModal
          dealId={dealId}
          dealName={dealName}
          isOpen={isShareModalOpen}
          onClose={handleCloseShareModal}
          prospects={[selectedProspectForShare]}
        />
      ) : null}

      {isOpportunityOwnerModalOpen ? (
        <OpportunityOwnerModal
          closeModal={() => {
            closeOpportunityOwnerModal();
          }}
          dealId={dealId}
          open={isOpportunityOwnerModalOpen}
          selectedProspects={selectedProspectsForOpportunityOwner}
        />
      ) : null}

      {dealOpportunityIdForEditingDataPoints ? (
        <UpdateDataPointsModal
          dealId={dealId}
          dealOpportunityId={dealOpportunityIdForEditingDataPoints}
          isOpen={isUpdateDataPointsModalOpen}
          onClose={closeUpdateDataPointsModal}
        />
      ) : null}

      {selectedProspectForContacts ? (
        <ContactsModal
          dealId={dealId}
          isOpen={isContactsModalOpen}
          limitedPartner={selectedProspectForContacts.limitedPartner}
          onClose={handleCloseContactsModal}
        />
      ) : null}

      <BulkActions
        isLoading={isLoading}
        onClear={() => setRowSelection({})}
        primaryAction={{
          children: m.coInvestments_outreach_bulkSendDeal(),
          onClick: () => handleEditEmail(Object.keys(rowSelection)),
          disabled: !canAllSelectedProspectsSendDeal,
        }}
        secondaryAction={{
          children: m.btn_addOpportunityOwner(),
          onClick: () => handleAddOpportunityOwner(Object.keys(rowSelection)),
        }}
        selectedCount={selectedCount}
        selectionLabel={{
          single: m.coInvestments_prospect(),
          multiple: m.coInvestments_prospects(),
        }}
      />
    </>
  );
};
